import React, { useState } from 'react';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { useAuth } from '@shared/hooks/useAuth';
import { useImpersonation } from '@shared/hooks/useImpersonation';
import { useToast } from '@shared/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';
import { Icon, IconSize } from '@shared/icons';

const ImpersonationBanner: React.FC = () => {
  const { user } = useAuth();
  const { isImpersonating, stopImpersonation } = useImpersonation();
  const theme = useTheme();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [isStopping, setIsStopping] = useState(false);

  if (!isImpersonating) {
    return null;
  }

  const handleStop = async () => {
    setIsStopping(true);
    try {
      await stopImpersonation();
      await queryClient.invalidateQueries();
      toast.success('Stopped impersonating');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to stop impersonation');
    } finally {
      setIsStopping(false);
    }
  };

  return (
    <Box
      sx={{
        zIndex: 1000,
        position: 'fixed',
        top: 20,
        right: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 0,
        p: 0.5,
        minHeight: 28,
        backgroundColor: 'background.paper',
        boxShadow: "0px 4px 12px 0px rgba(0, 0, 0, 0.08)",
        borderRadius: 100,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
  
      <Typography
        px={1}
        variant="caption"
        sx={{ color: 'text.secondary', fontWeight: 500, lineHeight: 1.4 }}
      >
        Viewing as {user?.name || user?.email || 'another user'}
      </Typography>
      <Tooltip title="Stop impersonation">
        <IconButton
          size="small"
          onClick={handleStop}
          disabled={isStopping}
        >
          <Icon icon="StopCircleContained" color={theme.palette.error.main} size={IconSize.MEDIUM} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default ImpersonationBanner;
